<template>
  <div class="estoque-racao maxcontainer container">
    <div v-if="loading && !hasStock" class="loading-hint">
      <span class="loading-dot"></span>
      Carregando estoque de ração...
    </div>

    <div v-else-if="!hasStock" class="empty-state">
      <p class="empty-state-title">{{ loadError ? 'Não foi possível carregar o estoque' : 'Sem dados de estoque de ração' }}</p>
      <p class="empty-state-text">
        {{ loadError || 'Não há galpões com silo SmartFeed nesta unidade.' }}
      </p>
    </div>

    <template v-else>
      <div class="metricas-principais">
        <div class="metrica-card">
          <h3>Estoque total de ração no campo (kg)</h3>
          <div class="metrica-value">{{ formatKg(totalWeight) }} kg</div>
          <p class="metrica-sub">{{ barnCountLabel }} · {{ siloCountLabel }}</p>
        </div>
        <div class="metrica-card">
          <h3>Estoque total de ração no campo (%)</h3>
          <div class="metrica-value">{{ totalWeightPercentage }}%</div>
        </div>
      </div>

      <div class="secao-inferior">
        <div class="silos-section">
          <div class="silos-section-header">
            <h3>{{ siloSectionTitle }}</h3>
            <label class="silos-section-toggle" :title="showSilosSection ? 'Esconder estoque por silo' : 'Mostrar estoque por silo'">
              <span class="silos-section-toggle-label">{{ showSilosSection ? 'Visível' : 'Oculto' }}</span>
              <input
                type="checkbox"
                v-model="showSilosSection"
                aria-label="Alternar visibilidade do estoque por silo"
              />
              <span class="silos-section-slider"></span>
            </label>
          </div>

          <div v-show="showSilosSection">
            <div class="silos-by-barn">
              <section
                v-for="barnGroup in siloGroups"
                :key="barnGroup.barnPk"
                class="silos-barn-row"
              >
                <button
                  type="button"
                  class="barn-summary-card"
                  :class="{ expanded: isBarnExpanded(barnGroup.barnPk) }"
                  :aria-expanded="isBarnExpanded(barnGroup.barnPk)"
                  @click="toggleBarnExpanded(barnGroup.barnPk)"
                >
                  <div class="barn-summary-header">
                    <div class="barn-summary-title-wrap">
                      <span class="barn-summary-label">{{ barnGroup.producer }} — {{ barnGroup.barnName }}</span>
                      <span class="barn-summary-meta">{{ siloCountText(barnGroup.siloCount) }}</span>
                    </div>
                    <div class="barn-summary-stats">
                      <span class="barn-summary-weight">{{ formatKg(barnGroup.stock) }} kg</span>
                      <span class="barn-summary-percent" :class="levelClass(barnGroup.percentage)">
                        {{ barnGroup.percentage }}%
                      </span>
                    </div>
                  </div>
                  <div class="barn-summary-footer">
                    <span class="barn-summary-hint">
                      {{ isBarnExpanded(barnGroup.barnPk) ? 'Recolher detalhes' : 'Ver detalhes por silo' }}
                    </span>
                    <span class="barn-summary-chevron" aria-hidden="true">
                      {{ isBarnExpanded(barnGroup.barnPk) ? '▲' : '▼' }}
                    </span>
                  </div>
                </button>

                <div v-if="isBarnExpanded(barnGroup.barnPk)" class="silos-grid">
                  <SensorFeedSiloHome
                    v-for="silo in barnGroup.silos"
                    :key="`${barnGroup.barnPk}-${silo.id}`"
                    :sensor="silo.rawSensor"
                    :barn="barnGroup.barn"
                    :farm-pk="barnGroup.farmPk"
                    embedded
                  />
                </div>
              </section>
            </div>
          </div>
        </div>

        <div class="prioridade-section">
          <h3>Prioridade de abastecimento real</h3>

          <div v-if="canSaveCoordinates || coordsMessage" class="coords-note">
            <p v-if="missingCoordCount">
              {{ missingCoordCount }} {{ missingCoordCount === 1 ? 'galpão usa' : 'galpões usam' }} coordenada de referência, ainda sem lat/lng no cadastro.
            </p>
            <button
              v-if="canSaveCoordinates"
              type="button"
              class="coords-button"
              :disabled="savingCoords"
              @click="saveMissingCoordinates"
            >
              {{ savingCoords ? 'Gravando coordenadas...' : 'Gravar coordenadas dos galpões' }}
            </button>
            <p v-if="coordsMessage" class="coords-message">{{ coordsMessage }}</p>
          </div>

          <div class="priority-tabs" role="tablist">
            <button
              type="button"
              class="priority-tab"
              role="tab"
              :aria-selected="activeTab === 'rank'"
              :class="{ active: activeTab === 'rank' }"
              @click="activeTab = 'rank'"
            >
              Ranking
            </button>
            <button
              type="button"
              class="priority-tab"
              role="tab"
              :aria-selected="activeTab === 'rota'"
              :class="{ active: activeTab === 'rota' }"
              @click="activeTab = 'rota'"
            >
              Rota de entrega
            </button>
          </div>

          <template v-if="activeTab === 'rank'">
            <div class="priority-scroll">
              <table class="priority-table">
                <thead>
                  <tr>
                    <th scope="col" class="rank-col">#</th>
                    <th scope="col">
                      <button type="button" class="sort-button" :aria-sort="sortAria('name')" @click="toggleSort('name')">
                        Unidade / Granja / Produtor <span class="sort-arrow">{{ sortArrow('name') }}</span>
                      </button>
                    </th>
                    <th scope="col" class="num">
                      <button type="button" class="sort-button" :aria-sort="sortAria('days')" @click="toggleSort('days')">
                        Dias para zero <span class="sort-arrow">{{ sortArrow('days') }}</span>
                      </button>
                    </th>
                    <th scope="col" class="num">
                      <button type="button" class="sort-button" :aria-sort="sortAria('ma')" @click="toggleSort('ma')">
                        Consumo diário<br>(média móvel dos últimos 7 dias) <span class="sort-arrow">{{ sortArrow('ma') }}</span>
                      </button>
                    </th>
                    <th scope="col" class="num">
                      <button type="button" class="sort-button" :aria-sort="sortAria('stock')" @click="toggleSort('stock')">
                        Estoque atual<br>(somatório de todos os silos) <span class="sort-arrow">{{ sortArrow('stock') }}</span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in sortedRows"
                    :key="row.barnPk"
                    :class="`s-${row.state}`"
                  >
                    <td class="rank-col">{{ index + 1 }}</td>
                    <td>
                      <div class="unit-label">{{ row.unit }}</div>
                      <div class="producer-name">{{ row.producer }}</div>
                      <div class="barn-meta">{{ row.barnName }} · {{ siloCountText(row.siloCount) }}</div>
                      <div v-if="staleLabel(row)" class="stale-flag">{{ staleLabel(row) }}</div>
                    </td>
                    <td class="num">
                      <div class="days-cell">
                        <template v-if="!row.historyReady">
                          <span class="chip chip-idle">Calculando</span>
                        </template>
                        <template v-else-if="row.state === 'sem_leitura' || row.state === 'sem_consumo'">
                          <span class="chip chip-idle">{{ stateLabel(row.state) }}</span>
                        </template>
                        <template v-else>
                          <b>{{ row.daysLabel }}</b>
                          <span class="chip" :class="chipClass(row.state)">{{ stateLabel(row.state) }}</span>
                        </template>
                      </div>
                    </td>
                    <td class="num">
                      <div v-if="!row.historyReady">…</div>
                      <div v-else-if="row.movingAvg == null">–</div>
                      <div v-else class="cons-cell">
                        <svg class="spark" viewBox="0 0 84 24" role="img" :aria-label="sparkLabel(row)">
                          <title>{{ sparkLabel(row) }}</title>
                          <polygon :points="sparkArea(row.daily)" fill="#5e7d42" opacity="0.15"></polygon>
                          <polyline :points="sparkLine(row.daily)" fill="none" stroke="#5e7d42" stroke-width="1.5" stroke-linejoin="round"></polyline>
                          <circle
                            v-if="sparkLast(row.daily)"
                            :cx="sparkLast(row.daily)[0]"
                            :cy="sparkLast(row.daily)[1]"
                            r="2.4"
                            fill="#5e7d42"
                          ></circle>
                        </svg>
                        <span>{{ formatKg(row.movingAvg) }} kg/dia</span>
                      </div>
                    </td>
                    <td class="num">{{ formatKg(row.stock) }} kg</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="formula-foot">
              <b>Como é calculado.</b>
              Dias para zero = estoque atual do galpão ÷ consumo médio diário. O consumo de cada dia é a queda de peso somada de todos os silos do galpão, a partir das leituras válidas do SmartFeed agregadas por hora; subidas acima de 1.500 kg em uma hora são tratadas como abastecimento e não entram na conta. A média usa os 7 dias completos anteriores ({{ windowText }}) e só é calculada com pelo menos 3 dias com leitura. O dia de hoje não entra porque está incompleto. Galpões com média abaixo de 100 kg/dia aparecem como sem consumo.
            </p>
          </template>

          <template v-else>
            <div class="route-params">
              <label>
                Ponto de partida (lat, long)
                <input v-model="originInput" type="text" class="route-input" placeholder="-26.026958, -53.066013" />
              </label>
              <label>
                Galpões que zeram em até
                <select v-model.number="horizon" class="route-input">
                  <option :value="3">3 dias</option>
                  <option :value="5">5 dias</option>
                  <option :value="7">7 dias</option>
                  <option :value="10">10 dias</option>
                  <option :value="15">15 dias</option>
                  <option :value="30">30 dias</option>
                </select>
              </label>
              <label>
                Cada entrega cobre
                <select v-model.number="cover" class="route-input">
                  <option :value="5">5 dias</option>
                  <option :value="7">7 dias</option>
                  <option :value="10">10 dias</option>
                  <option :value="fillCover">até encher o silo</option>
                </select>
              </label>
              <label>
                Capacidade do caminhão (kg)
                <input v-model.number="truckKg" type="number" min="5000" step="100" class="route-input" />
              </label>
            </div>

            <p v-if="!parsedOrigin" class="route-empty">
              Ponto de partida inválido. Use o formato latitude, longitude.
            </p>
            <template v-else>
              <div v-if="trips.length" class="route-summary">
                <span><b>{{ trips.length }}</b> {{ trips.length === 1 ? 'viagem' : 'viagens' }}</span>
                <span><b>{{ barnsServed }}</b> {{ barnsServed === 1 ? 'galpão atendido' : 'galpões atendidos' }}</span>
                <span><b>{{ formatKg(totalRouteKg) }}</b> kg de ração</span>
                <span><b>~{{ formatKg(totalRouteKm) }}</b> km estimados</span>
              </div>
              <p v-else class="route-empty">
                Nenhum galpão zera em até {{ horizon }} dias. Aumente o prazo para planejar mais entregas.
              </p>

              <div class="route-layout">
                <FeedInventoryRouteMap
                  :origin="parsedOrigin"
                  :origin-label="originMapLabel"
                  :markers="producerMarkers"
                  :trips="mapTrips"
                  :selected-trip="selectedTrip"
                  @select="toggleTrip"
                />
                <div>
                  <button
                    v-for="(trip, index) in trips"
                    :key="`trip-${index}`"
                    type="button"
                    class="trip-card"
                    :class="{ selected: selectedTrip === index }"
                    :style="{ '--tc': tripColor(index) }"
                    :aria-pressed="selectedTrip === index"
                    @click="toggleTrip(index)"
                  >
                    <div class="trip-head">
                      <span>Viagem {{ index + 1 }}{{ trip.full ? ' · carga cheia' : '' }}</span>
                      <span>{{ formatKg(trip.load) }} kg · ~{{ formatKg(Math.round(trip.km)) }} km</span>
                    </div>
                    <ol>
                      <li v-for="(stop, stopIndex) in trip.stops" :key="`${index}-${stop.row.barnPk}-${stopIndex}`">
                        {{ stop.row.producer }} · {{ stop.row.barnName }}
                        <span> · {{ formatKg(stop.load) }} kg · zera em {{ formatDays(stop.row.daysToZero) }} d</span>
                      </li>
                    </ol>
                    <div class="trip-foot">
                      Prazo: {{ formatDays(trip.deadline) }} dias · ocupação do caminhão {{ truckOccupancy(trip) }}%
                    </div>
                  </button>
                </div>
              </div>
            </template>

            <p class="formula-foot">
              <b>Como a rota é montada.</b>
              Entram os galpões que zeram dentro do prazo escolhido. A quantidade de cada entrega é o consumo médio × os dias de cobertura, limitada ao espaço livre nos silos. Pedidos maiores que o caminhão saem em carga cheia direta (linha tracejada); o restante é agrupado em viagens, começando pelo galpão mais urgente e somando os vizinhos mais próximos que cabem na carga. A ordem das paradas minimiza a distância. Distâncias em linha reta × 1,3, como estimativa de estrada. O ponto de partida é {{ originDescription }}.
            </p>
          </template>
        </div>
      </div>
    </template>

    <div
      v-if="showLoadCard"
      class="load-card"
      :class="{ 'load-card--done': loadComplete, 'load-card--leave': loadCardLeaving }"
    >
      <div class="load-card-head">
        <span v-if="!loadComplete" class="loading-dot"></span>
        <span class="load-card-title">{{ loadComplete ? 'Carregado' : 'Carregando' }}</span>
        <span class="load-card-percent">{{ loadProgress }}%</span>
      </div>
      <div class="load-bar" aria-hidden="true">
        <div class="load-bar-fill" :style="{ width: loadProgress + '%' }"></div>
      </div>
      <p class="load-card-detail">{{ loadCardDetail }}</p>
    </div>
  </div>
</template>

<script>
import FeedInventoryRouteMap from './FeedInventoryRouteMap.vue'
import SensorFeedSiloHome from './SensorFeedSiloHome.vue'
import { companyOrigin, isBrfCompany } from '../utils/brfBarnCoordinates'
import { STATE_LABELS, formatStaleDate } from '../utils/feedConsumption'
import { FILL_SILO_COVER, buildRoutes, parseOriginInput } from '../utils/feedDeliveryRoute'
import { loadCompanyFeedInventory, mapPool } from '../utils/feedInventoryLoad'
import { resolveFeedInventoryAccess } from '../utils/feedInventoryAccess'

const SILOS_KEY = 'feedInventory.showSilos'
const ORIGIN_KEY = (companyId) => `feedInventory.origin.${companyId}`
const TRIP_COLORS = ['#1565c0', '#6a1b9a', '#00838f', '#ef6c00', '#ad1457']
const STATE_RANK = {
  critico: 0,
  atencao: 1,
  folga: 2,
  sem_consumo: 3,
  sem_leitura: 3,
}

export default {
  name: 'FeedInventoryPage',
  components: {
    FeedInventoryRouteMap,
    SensorFeedSiloHome,
  },
  props: {
    barn: {
      type: Object,
      default: null,
    },
    batch: {
      type: Object,
      default: null,
    },
    viewAllBarns: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: true,
      loadingHistory: false,
      loadError: '',
      loadFailed: false,
      loadTotal: 0,
      loadDone: 0,
      loadTotalKnown: false,
      loadCardDismissed: false,
      loadCardLeaving: false,
      loadCardTimer: null,
      companyName: '',
      stockRows: [],
      priorityRows: [],
      showSilosSection: false,
      expandedBarnPks: [],
      collapsedBarnPks: [],
      activeTab: 'rank',
      sortKey: 'days',
      sortDir: 1,
      originInput: '',
      horizon: 10,
      cover: 7,
      truckKg: 18000,
      selectedTrip: null,
      savingCoords: false,
      coordsMessage: '',
      requestToken: 0,
      fillCover: FILL_SILO_COVER,
    }
  },
  computed: {
    companyId() {
      const user = this.$store.getters.getCurrentUser
      const targetCompany = this.$store.getters.getTargetCompany
      if (this.$store.getters.getUserLevel === 'pecsmart' && targetCompany) {
        return targetCompany
      }
      return user?.company ?? null
    },
    feedInventoryAccess() {
      return resolveFeedInventoryAccess(
        this.$store.getters.getCurrentUser,
        this.$store.getters.getCompanySettings,
      )
    },
    siloSectionTitle() {
      return this.feedInventoryAccess.farmPks?.length
        ? 'Estoque por silo'
        : 'Estoque por silo, todos os galpões'
    },
    expandBarnsByDefault() {
      const role = this.$store.getters.getCurrentUser?.role || ''
      return !role.startsWith('company_')
    },
    feedFarmScope() {
      const access = this.feedInventoryAccess
      return [
        this.companyId,
        access.allowed ? 1 : 0,
        access.farmPk || '',
        (access.farmPks || []).join(','),
      ].join('|')
    },
    hasStock() {
      return this.stockRows.length > 0
    },
    loadComplete() {
      return !this.loading && !this.loadingHistory && this.hasStock && !this.loadFailed
    },
    showLoadCard() {
      return (this.loading || this.loadingHistory || this.loadComplete) && !this.loadError && !this.loadCardDismissed
    },
    loadProgress() {
      if (this.loadComplete) return 100
      if (!this.loadTotalKnown || this.loadTotal <= 0) return 0
      return Math.min(99, Math.round((this.loadDone / this.loadTotal) * 100))
    },
    loadCardDetail() {
      if (this.loadComplete) return `${this.barnCountLabel} · ${this.siloCountLabel}`
      if (!this.loadTotalKnown || !this.loadTotal) return 'Buscando galpões da unidade'
      return `${this.loadDone} de ${this.loadTotal} galpões`
    },
    totalWeight() {
      return this.stockRows.reduce((sum, row) => sum + (row.stock || 0), 0)
    },
    totalCapacity() {
      return this.stockRows.reduce((sum, row) => sum + (row.capacity || 0), 0)
    },
    totalWeightPercentage() {
      if (!this.totalCapacity) return 0
      return Math.round((this.totalWeight / this.totalCapacity) * 100)
    },
    barnCountLabel() {
      const count = this.stockRows.length
      return `${count} ${count === 1 ? 'galpão' : 'galpões'}`
    },
    siloCountLabel() {
      return this.siloCountText(this.stockRows.reduce((sum, row) => sum + (row.siloCount || 0), 0))
    },
    siloGroups() {
      return [...this.stockRows].sort((a, b) => (
        `${a.producer} ${a.barnName}`.localeCompare(`${b.producer} ${b.barnName}`, 'pt-BR')
      ))
    },
    sortedRows() {
      const rows = [...this.stockRows]
      const direction = this.sortDir
      const rank = (row) => {
        if (!row.historyReady) return 1e13
        if (row.state === 'sem_leitura') return 1e12
        if (row.state === 'sem_consumo') return 1e9 + row.stock
        return row.daysToZero
      }
      rows.sort((a, b) => {
        if (this.sortKey === 'name') {
          return `${a.producer} ${a.barnName}`.localeCompare(`${b.producer} ${b.barnName}`, 'pt-BR') * direction
        }
        if (this.sortKey === 'days') return (rank(a) - rank(b)) * direction
        if (this.sortKey === 'ma') {
          if (!a.historyReady && !b.historyReady) return 0
          if (!a.historyReady) return 1
          if (!b.historyReady) return -1
          return ((a.movingAvg ?? -1) - (b.movingAvg ?? -1)) * direction
        }
        return ((a.stock ?? -1) - (b.stock ?? -1)) * direction
      })
      return rows
    },
    windowText() {
      const days = this.stockRows[0]?.days || []
      if (days.length < 2) return 'os 7 dias completos anteriores'
      return `${this.formatDayLabel(days[0])} a ${this.formatDayLabel(days[days.length - 1])}`
    },
    parsedOrigin() {
      return parseOriginInput(this.originInput)
    },
    trips() {
      if (!this.parsedOrigin) return []
      const ready = this.stockRows.filter((row) => row.historyReady)
      return buildRoutes(ready, this.parsedOrigin, this.horizon, this.cover, this.truckKg)
    },
    barnsServed() {
      return new Set(this.trips.flatMap((trip) => trip.stops.map((stop) => stop.row.barnPk))).size
    },
    totalRouteKg() {
      return this.trips.reduce((sum, trip) => sum + trip.load, 0)
    },
    totalRouteKm() {
      return Math.round(this.trips.reduce((sum, trip) => sum + trip.km, 0))
    },
    producerMarkers() {
      const groups = new Map()
      this.stockRows.forEach((row) => {
        if (row.lat == null || row.lng == null) return
        const key = row.farmPk != null ? String(row.farmPk) : (row.producer || String(row.barnPk))
        const rank = STATE_RANK[row.state] ?? 3
        const current = groups.get(key)
        if (!current) {
          groups.set(key, {
            key,
            name: row.producer,
            lat: row.lat,
            lng: row.lng,
            state: row.state,
            count: 1,
            municipality: row.municipality,
            rank,
          })
          return
        }
        current.count += 1
        if (rank < current.rank) {
          current.rank = rank
          current.state = row.state
        }
      })
      return [...groups.values()]
    },
    mapTrips() {
      if (!this.parsedOrigin) return []
      return this.trips.map((trip, index) => ({
        full: trip.full,
        color: this.tripColor(index),
        positions: [
          this.parsedOrigin,
          ...trip.stops.map((stop) => [stop.row.lat, stop.row.lng]),
          this.parsedOrigin,
        ],
      }))
    },
    originDescription() {
      const preset = companyOrigin(this.companyId)
      const parsed = this.parsedOrigin
      if (
        preset
        && parsed
        && Math.abs(parsed[0] - preset.lat) < 1e-5
        && Math.abs(parsed[1] - preset.lng) < 1e-5
      ) {
        return `${preset.label} (${preset.address})`
      }
      return 'o ponto de partida informado acima'
    },
    originMapLabel() {
      return companyOrigin(this.companyId)?.label || 'Partida'
    },
    isPecsmartAdmin() {
      return this.$store.getters.getCurrentUser?.role === 'pecsmart_admin'
    },
    missingCoordCount() {
      return this.stockRows.filter((row) => row.coordSource === 'csv').length
    },
    canSaveCoordinates() {
      return this.isPecsmartAdmin
        && isBrfCompany(this.companyId)
        && !this.loadingHistory
        && this.missingCoordCount > 0
    },
  },
  watch: {
    companyId(next, prev) {
      if (next == null || next === prev) return
      this.syncOrigin()
      this.loadUnit()
    },
    feedFarmScope(next, prev) {
      if (next === prev) return
      this.loadUnit()
    },
    showSilosSection(value) {
      this.writeStorage(SILOS_KEY, value ? '1' : '0')
    },
    originInput(value) {
      if (this.companyId == null) return
      this.writeStorage(ORIGIN_KEY(this.companyId), value == null ? '' : String(value))
      this.selectedTrip = null
    },
    horizon() {
      this.selectedTrip = null
    },
    cover() {
      this.selectedTrip = null
    },
    truckKg() {
      this.selectedTrip = null
    },
    loadComplete(done) {
      this.clearLoadCardTimer()
      if (!done) return
      this.loadCardTimer = setTimeout(() => {
        this.loadCardLeaving = true
        this.loadCardTimer = setTimeout(() => {
          this.loadCardDismissed = true
          this.loadCardLeaving = false
          this.loadCardTimer = null
        }, 400)
      }, 3000)
    },
    trips(next) {
      if (this.selectedTrip != null && this.selectedTrip >= next.length) {
        this.selectedTrip = null
      }
    },
  },
  created() {
    const storedSilos = this.readStorage(SILOS_KEY)
    this.showSilosSection = storedSilos == null ? this.expandBarnsByDefault : storedSilos === '1'
    this.syncOrigin()
    this.loadUnit()
  },
  beforeDestroy() {
    this.clearLoadCardTimer()
  },
  methods: {
    clearLoadCardTimer() {
      if (!this.loadCardTimer) return
      clearTimeout(this.loadCardTimer)
      this.loadCardTimer = null
    },
    readStorage(key) {
      try {
        return window.localStorage.getItem(key)
      } catch (error) {
        return null
      }
    },
    writeStorage(key, value) {
      try {
        window.localStorage.setItem(key, value)
      } catch (error) {
        /* armazenamento indisponível */
      }
    },
    syncOrigin() {
      const stored = this.companyId == null ? null : this.readStorage(ORIGIN_KEY(this.companyId))
      if (stored != null) {
        this.originInput = stored
        return
      }
      const preset = companyOrigin(this.companyId)
      this.originInput = preset ? `${preset.lat}, ${preset.lng}` : ''
    },
    async loadUnit() {
      const { allowed, farmPk, farmPks } = this.feedInventoryAccess
      if (this.companyId == null || !allowed) {
        this.loading = false
        this.loadingHistory = false
        this.stockRows = []
        this.priorityRows = []
        return
      }

      const token = this.requestToken + 1
      this.requestToken = token
      this.loading = true
      this.loadingHistory = true
      this.loadError = ''
      this.loadFailed = false
      this.loadTotal = 0
      this.loadDone = 0
      this.loadTotalKnown = false
      this.loadCardDismissed = false
      this.loadCardLeaving = false
      this.clearLoadCardTimer()
      this.stockRows = []
      this.priorityRows = []
      this.selectedTrip = null
      this.coordsMessage = ''

      try {
        const result = await loadCompanyFeedInventory({
          companyId: this.companyId,
          farmPk,
          farmPks,
          headers: this.$store.getters.getHeader,
          onBarn: (payload) => {
            if (token !== this.requestToken) return
            this.companyName = payload.companyName
            this.upsertBarnRow(payload.row)
            this.loading = false
          },
          onProgress: (progress) => {
            if (token !== this.requestToken) return
            this.loadTotalKnown = progress.known
            this.loadTotal = progress.total
            this.loadDone = progress.done
          },
        })
        if (token !== this.requestToken) return
        this.companyName = result.companyName
      } catch (error) {
        console.error('Feed inventory: error loading unit', error)
        if (token !== this.requestToken) return
        this.loadFailed = true
        if (!this.stockRows.length) {
          this.loadError = 'Não foi possível carregar o estoque desta unidade.'
        }
      } finally {
        if (token === this.requestToken) {
          this.loading = false
          this.loadingHistory = false
        }
      }
    },
    upsertBarnRow(row) {
      const index = this.stockRows.findIndex((item) => String(item.barnPk) === String(row.barnPk))
      if (index === -1) this.stockRows.push(row)
      else this.$set(this.stockRows, index, row)
      this.priorityRows = this.stockRows
    },
    async saveMissingCoordinates() {
      if (this.savingCoords || !this.canSaveCoordinates) return
      const targets = this.stockRows.filter((row) => row.coordSource === 'csv' && row.lat != null && row.lng != null)
      this.savingCoords = true
      this.coordsMessage = ''
      let saved = 0
      let failed = 0
      await mapPool(targets, 4, async (row) => {
        const ok = await this.$store.dispatch('updateBarnCoordinates', {
          barnPk: row.barnPk,
          lat: row.lat,
          lng: row.lng,
        })
        if (ok) {
          saved += 1
          row.coordSource = 'api'
        } else {
          failed += 1
        }
      })
      this.savingCoords = false
      this.stockRows = [...this.stockRows]
      if (this.priorityRows === this.stockRows) {
        this.priorityRows = this.stockRows
      } else {
        this.priorityRows = this.priorityRows.map((row) => {
          const match = targets.find((target) => target.barnPk === row.barnPk && target.coordSource === 'api')
          return match ? { ...row, coordSource: 'api' } : row
        })
      }
      if (failed) {
        this.coordsMessage = saved
          ? `Coordenadas gravadas em ${saved} galpões. ${failed} não puderam ser atualizados.`
          : 'Não foi possível gravar as coordenadas.'
      } else {
        this.coordsMessage = `Coordenadas gravadas em ${saved} ${saved === 1 ? 'galpão' : 'galpões'}.`
      }
    },
    toggleSort(key) {
      if (this.sortKey === key) this.sortDir *= -1
      else {
        this.sortKey = key
        this.sortDir = 1
      }
    },
    sortArrow(key) {
      if (this.sortKey !== key) return '▲'
      return this.sortDir > 0 ? '▲' : '▼'
    },
    sortAria(key) {
      if (this.sortKey !== key) return 'none'
      return this.sortDir > 0 ? 'ascending' : 'descending'
    },
    stateLabel(state) {
      return STATE_LABELS[state] || ''
    },
    chipClass(state) {
      if (state === 'critico') return 'chip-crit'
      if (state === 'atencao') return 'chip-warn'
      if (state === 'folga') return 'chip-ok'
      return 'chip-idle'
    },
    staleLabel(row) {
      const stale = row.staleSilos || []
      if (!stale.length) return ''
      const dates = [...new Set(stale.map((silo) => formatStaleDate(silo.lastFetched) || 'o cadastro'))]
      const noun = stale.length === 1 ? '1 silo' : `${stale.length} silos`
      return `⚠ ${noun} sem leitura desde ${dates.join(', ')}`
    },
    sparkPoints(daily) {
      const values = (daily || []).map((value) => (value == null ? 0 : value))
      if (values.length < 2) return []
      const width = 84
      const height = 24
      const max = Math.max(...values, 1)
      const step = width / (values.length - 1)
      return values.map((value, index) => {
        const x = index * step
        const y = height - 2 - (value / max) * (height - 6)
        return [Number(x.toFixed(1)), Number(y.toFixed(1))]
      })
    },
    sparkLine(daily) {
      return this.sparkPoints(daily).map((point) => point.join(',')).join(' ')
    },
    sparkArea(daily) {
      const line = this.sparkLine(daily)
      if (!line) return ''
      return `0,24 ${line} 84,24`
    },
    sparkLast(daily) {
      const points = this.sparkPoints(daily)
      return points.length ? points[points.length - 1] : null
    },
    sparkLabel(row) {
      const parts = (row.daily || []).map((value, index) => {
        const day = row.days?.[index] ? this.formatDayLabel(row.days[index]) : ''
        const text = value == null ? 'sem leitura' : this.formatKg(value)
        return `${day}: ${text}`
      })
      return `Consumo diário (kg): ${parts.join(' · ')}`
    },
    formatDayLabel(isoDate) {
      const parts = String(isoDate || '').split('-')
      if (parts.length < 3) return isoDate
      return `${parts[2]}/${parts[1]}`
    },
    formatKg(value) {
      return Number(value || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
    },
    formatDays(value) {
      return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
    },
    siloCountText(count) {
      return `${count} ${count === 1 ? 'silo' : 'silos'}`
    },
    isBarnExpanded(barnPk) {
      const key = String(barnPk)
      if (this.expandedBarnPks.includes(key)) return true
      if (this.collapsedBarnPks.includes(key)) return false
      return this.expandBarnsByDefault
    },
    toggleBarnExpanded(barnPk) {
      const key = String(barnPk)
      if (this.isBarnExpanded(barnPk)) {
        const index = this.expandedBarnPks.indexOf(key)
        if (index >= 0) this.expandedBarnPks.splice(index, 1)
        if (!this.collapsedBarnPks.includes(key)) this.collapsedBarnPks.push(key)
        return
      }
      const index = this.collapsedBarnPks.indexOf(key)
      if (index >= 0) this.collapsedBarnPks.splice(index, 1)
      if (!this.expandedBarnPks.includes(key)) this.expandedBarnPks.push(key)
    },
    levelClass(level) {
      if (level > 100) return 'overfill'
      if (level > 30) return 'good'
      if (level > 15) return 'medium'
      if (level > 0) return 'low'
      return 'empty'
    },
    tripColor(index) {
      return TRIP_COLORS[index % TRIP_COLORS.length]
    },
    truckOccupancy(trip) {
      const truck = Math.max(5000, Number(this.truckKg) || 18000)
      return Math.round((trip.load / truck) * 100)
    },
    toggleTrip(index) {
      this.selectedTrip = this.selectedTrip === index ? null : index
    },
  },
}
</script>

<style scoped>
.estoque-racao {
  padding: 20px 0;
}

.loading-hint,
.silos-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 0.9rem;
}

.prioridade-section .silos-loading {
  margin: 0 0 16px;
}

.load-card {
  position: fixed;
  right: 20px;
  bottom: 24px;
  z-index: 40;
  width: min(340px, calc(100vw - 32px));
  padding: 16px 18px;
  background: #f5b942;
  color: #3d2e00;
  border-radius: 4px;
  border-left: 8px solid #e09a12;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  animation: load-card-in 0.45s ease forwards;
}

.load-card--done {
  background: #81c784;
  border-left-color: #4caf50;
  color: #fff;
}

.load-card--leave {
  animation: load-card-out 0.4s ease forwards;
}

.load-card .loading-dot {
  background: currentColor;
}

.load-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
}

.load-card-title {
  color: inherit;
  font-weight: 700;
}

.load-card-percent {
  margin-left: auto;
  color: inherit;
  font-weight: 700;
}

.load-bar {
  margin-top: 10px;
  height: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.35);
  overflow: hidden;
}

.load-bar-fill {
  height: 100%;
  border-radius: 999px;
  background: rgba(61, 46, 0, 0.35);
  transition: width 0.25s ease;
}

.load-card--done .load-bar-fill {
  background: #fff;
}

.load-card-detail {
  margin: 8px 0 0;
  color: inherit;
  font-size: 0.85rem;
  opacity: 0.9;
}

@keyframes load-card-in {
  from {
    transform: translateX(120%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@keyframes load-card-out {
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(120%);
    opacity: 0;
  }
}

.loading-hint {
  justify-content: center;
  padding: 40px 0;
  min-height: 200px;
}

.loading-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #78b843;
  animation: pulse 1s ease-in-out infinite;
}

.empty-state,
.route-empty {
  text-align: center;
  padding: 24px;
  background: white;
  border-radius: 10px;
  color: #6c757d;
}

.empty-state-title {
  color: #2c3e50;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 8px;
}

.empty-state-text,
.route-empty {
  margin: 0 0 16px;
}

.metricas-principais {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 40px;
}

.metrica-card {
  background: white;
  border: 2px solid #ffa500;
  border-radius: 10px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.metrica-card h3 {
  color: #2c3e50;
  font-size: 1rem;
  margin-bottom: 15px;
  font-weight: 600;
}

.metrica-value {
  font-size: 3rem;
  font-weight: bold;
  color: #2c3e50;
}

.metrica-sub {
  margin: 8px 0 0;
  color: #6c757d;
  font-size: 0.9rem;
}

.secao-inferior {
  display: grid;
  gap: 30px;
}

.silos-section,
.prioridade-section {
  background: white;
  border-radius: 10px;
  padding: 25px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.silos-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.silos-section-header h3,
.prioridade-section h3 {
  color: #2c3e50;
  font-size: 1.2rem;
  margin: 0 0 20px;
  font-weight: 600;
}

.silos-section-header h3 {
  margin: 0;
}

.silos-section-header + * {
  margin-top: 20px;
}

.silos-section-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  margin: 0;
  flex-shrink: 0;
}

.silos-section-toggle-label {
  color: #6c757d;
  font-size: 0.85rem;
  font-weight: 500;
}

.silos-section-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.silos-section-slider {
  position: relative;
  width: 44px;
  height: 24px;
  background: #d1d5db;
  border-radius: 999px;
  transition: background 0.2s ease;
}

.silos-section-slider::before {
  content: '';
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  background: white;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}

.silos-section-toggle input:checked + .silos-section-slider {
  background: #78b843;
}

.silos-section-toggle input:checked + .silos-section-slider::before {
  transform: translateX(20px);
}

.silos-by-barn {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.silos-barn-row {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.barn-summary-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  padding: 16px 18px;
  border: 1px solid #e3e8ec;
  border-radius: 12px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafb 100%);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  text-align: left;
}

.barn-summary-card.expanded {
  border-color: #87da44;
}

.barn-summary-header,
.barn-summary-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.barn-summary-header {
  align-items: flex-start;
}

.barn-summary-title-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.barn-summary-label,
.producer-name {
  color: #2c3e50;
  font-weight: 700;
}

.barn-summary-meta,
.barn-meta,
.unit-label {
  color: #6c757d;
  font-size: 0.82rem;
}

.barn-summary-weight {
  color: #2c3e50;
  font-weight: 700;
  white-space: nowrap;
  padding: 4px 10px;
  border-radius: 999px;
  background: #eef6e8;
  border: 1px solid #d7e8c8;
}

.barn-summary-percent {
  font-weight: 700;
}

.barn-summary-percent.good { color: #415430; }
.barn-summary-percent.medium { color: #8a6d1f; }
.barn-summary-percent.low { color: #952323; }
.barn-summary-percent.overfill { color: #1565c0; }
.barn-summary-percent.empty { color: #6c757d; }

.silos-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
}

.coords-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  margin-bottom: 16px;
  color: #6c757d;
  font-size: 0.88rem;
}

.coords-note p {
  margin: 0;
}

.coords-button,
.priority-tab,
.trip-card,
.sort-button {
  font: inherit;
}

.coords-button {
  border: 1px solid #78b843;
  background: #fff;
  color: #3d6b18;
  border-radius: 8px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 600;
}

.coords-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.priority-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.priority-tab {
  border: 1px solid #e5e7eb;
  background: #fff;
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  font-weight: 600;
  color: #2c3e50;
}

.priority-tab.active {
  background: #78b843;
  border-color: #78b843;
  color: #fff;
}

.priority-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.priority-table {
  width: 100%;
  min-width: 760px;
  border-collapse: collapse;
}

.priority-table th,
.priority-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #eef1f3;
  vertical-align: top;
  text-align: left;
}

.priority-table th {
  color: #6c757d;
  font-size: 0.82rem;
  font-weight: 700;
}

.priority-table .num,
.priority-table .rank-col {
  white-space: nowrap;
}

.priority-table .num {
  text-align: right;
}

.sort-button {
  background: none;
  border: 0;
  padding: 0;
  color: inherit;
  font-weight: 700;
  cursor: pointer;
  text-align: inherit;
}

.num .sort-button {
  text-align: right;
}

.sort-arrow {
  color: #78b843;
}

.priority-table tr.s-critico td:first-child { box-shadow: inset 4px 0 0 #c62828; }
.priority-table tr.s-atencao td:first-child { box-shadow: inset 4px 0 0 #ef6c00; }
.priority-table tr.s-folga td:first-child { box-shadow: inset 4px 0 0 #2e7d32; }
.priority-table tr.s-sem_consumo td:first-child,
.priority-table tr.s-sem_leitura td:first-child { box-shadow: inset 4px 0 0 #9e9e9e; }

.days-cell,
.cons-cell {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.chip {
  display: inline-block;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 700;
}

.chip-crit { background: #fdecea; color: #c62828; }
.chip-warn { background: #fff4e5; color: #e65100; }
.chip-ok { background: #e8f5e9; color: #2e7d32; }
.chip-idle { background: #f3f4f6; color: #6b7280; }

.spark {
  width: 84px;
  height: 24px;
  flex-shrink: 0;
}

.stale-flag {
  margin-top: 4px;
  color: #8a6d1f;
  font-size: 0.78rem;
}

.formula-foot {
  color: #6c757d;
  font-size: 0.82rem;
  margin: 14px 0 0;
  max-width: 80ch;
}

.formula-foot b {
  color: #2c3e50;
  font-weight: 600;
}

.route-params {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.route-params label {
  display: flex;
  flex-direction: column;
  gap: 6px;
  color: #6c757d;
  font-size: 0.82rem;
  font-weight: 600;
}

.route-input {
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  color: #2c3e50;
  font-size: 0.95rem;
  font-weight: 500;
  background: #fff;
}

.route-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 16px;
  color: #2c3e50;
}

.route-layout {
  display: grid;
  grid-template-columns: minmax(280px, 1.1fr) minmax(260px, 0.9fr);
  gap: 16px;
  align-items: start;
}

.trip-card {
  display: block;
  width: 100%;
  text-align: left;
  border: 1px solid #e5e7eb;
  border-left: 4px solid var(--tc);
  border-radius: 10px;
  padding: 12px 14px;
  background: #fff;
  cursor: pointer;
  margin-bottom: 10px;
  color: #2c3e50;
}

.trip-card.selected {
  box-shadow: 0 0 0 2px var(--tc);
}

.trip-head,
.trip-foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.85rem;
}

.trip-head {
  font-weight: 700;
  margin-bottom: 8px;
}

.trip-card ol {
  margin: 0 0 8px;
  padding-left: 18px;
}

.trip-card li span,
.trip-foot {
  color: #6c757d;
  font-weight: 500;
}

@keyframes pulse {
  0%, 100% { opacity: 0.4; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.1); }
}

@media (max-width: 900px) {
  .metricas-principais,
  .route-params,
  .route-layout {
    grid-template-columns: 1fr;
  }

  .metrica-value {
    font-size: 2rem;
  }

  .silos-grid {
    grid-template-columns: 1fr;
  }
}
</style>
